/**
 * CORE-AC — the disclosure that sits beside a displayed finish time.
 *
 * CORE-AC's remaining constitutional item: a finish time that inherits a
 * technician's efficiency must disclose when that efficiency was **manually
 * asserted rather than learned**. CORE-G's law adds the third case — a value
 * "derived from insufficient evidence" must not be presented "as though it were
 * established" — and the 2026-09-07 ruling names it outright: missing
 * efficiency is unknown, not 100%.
 *
 * So this renders three states, and the one that does the most work is
 * `unrecorded`. A technician created by an invitation, a role change or an
 * untouched roster row carries 100 because a row needs a number. The time
 * inheriting it is still shown — the ruling permits a scoped fallback — but it
 * is labelled an estimate rather than allowed to read as a measurement.
 *
 * It takes the technician's own fields rather than a pre-resolved label, so
 * there is exactly one derivation path and a caller cannot hand it a
 * description of some other number.
 */

import { describeEfficiencyBasis, type EfficiencyBasisInput } from '@/utils/efficiencyDisclosure';
import { cn } from '@/lib/utils';

interface EfficiencyBasisBadgeProps extends EfficiencyBasisInput {
  className?: string;
}

export function EfficiencyBasisBadge({
  efficiencyPercentage,
  efficiencySource,
  efficiencySampleSize,
  className,
}: EfficiencyBasisBadgeProps) {
  const basis = describeEfficiencyBasis({
    efficiencyPercentage,
    efficiencySource,
    efficiencySampleSize,
  });

  return (
    <div
      className={cn(
        'flex items-center justify-end text-[10px] text-muted-foreground',
        className,
      )}
      data-testid="ticket-optitime-basis"
      data-basis={basis.basis}
      data-established={basis.isEstablished ? 'true' : 'false'}
      data-provisional={basis.isProvisional ? 'true' : 'false'}
      title={basis.detail}
    >
      <span aria-label={basis.detail}>
        {/* An efficiency nobody derived produces an estimate, and the word
            appears before the label so it is read first. */}
        {basis.isEstablished || basis.isProvisional ? null : <span>Estimate · </span>}
        {basis.label}
      </span>
    </div>
  );
}
